import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Store, select } from '@ngrx/store';
import {
  DialogService,
  DynamicDialogConfig,
  DynamicDialogRef,
} from 'primeng/dynamicdialog';
import { MediaType } from '../../../enums/media-type';
import { MergeFieldContent } from '../../../enums/merge-field-content';
import { MediaSelectorComponent } from '../../../media/components/media-selector/media-selector.component';
import { Client } from '../../../models/Client';
import { Media } from '../../../models/Media';
import { Template } from '../../../models/Template';
import { EnvironmentService } from '../../../services/environment.service';
import { AppState } from '../../../state/reducers';
import { TemplateType } from '../../../enums/template-type';
import { ConversationService } from '../../services/conversation.service';
import { TemplateMergeField } from '../../../models/TemplateMergeField';
import { Patient } from "../../../models/Patient";
import { Theme } from "../../../enums/theme";
import { getCurrentPractice } from '../../../practices/state/selectors';
import { Observable, takeWhile } from 'rxjs';
import { isEmbedMode, getCurrencies } from '../../../state/selectors';
import { TranslateService } from '@ngx-translate/core';
import { Currency } from '../../../models/Currency';

@Component({
  selector: 'replace-placeholder',
  templateUrl: './replace-placeholder.component.html',
  styleUrls: ['./replace-placeholder.component.scss'],
  providers: [DialogService],
})
export class ReplacePlaceholderComponent implements OnInit, OnDestroy {
  templateType = TemplateType;
  buttonClass = '';
  template?: Template;
  manualPlaceholders?: {id: number, label: string}[] = [];
  client?: Client;
  patient?: Patient;
  placeholdersForm = new UntypedFormGroup({});
  buttonLinkForm = new UntypedFormGroup({
    buttonLink: new UntypedFormControl('', Validators.required)
  });
  selectMediaDialog?: DynamicDialogRef;
  previewSrc: string | null = null;
  previewType = 'image';
  selectedMedia?: Media;
  mediaName = '';
  alive = true;
  mediaError: string | null = null;
  mediaType = MediaType;
  selectedMediaType: MediaType | null = null;
  mediaTypes: MediaType[] = Object.values(MediaType);
  error = false;
  result:
    | {
        template: Template;
        placeholders: { placeholderId: string; replacement: string }[];
        media?: Media;
        previewType?: string;
        buttonLink?: string;
      }
    | undefined;
  device = 'desktop';
  previewOpen = false;
  helpLink = '';
  theme: string = Theme.DigitalPractice;
  embedMode$: Observable<boolean>;
  currencies: Currency[] = [];
  practiceCurrency?: Currency;
  practice: any;

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private store: Store<AppState>,
    public dialogService: DialogService,
    public environmentService: EnvironmentService,
    private conversationService: ConversationService,
    private translateService: TranslateService,
  ) {
    this.template = this.config.data.template;
    this.manualPlaceholders = this.config.data.manualPlaceholders;
    this.client = this.config.data.client;
    this.patient = this.config.data.patient;
    this.buttonClass = this.config.data.buttonClass;

    this.embedMode$ = this.store.pipe(select(isEmbedMode)).pipe(takeWhile(() => this.alive));

    if (this.template?.defaultMedia) {
      this.setSelectedMedia(this.template.defaultMedia);
    }
    this.subscribeToCurrentPractice();
    this.subscribeToCurrencies();
  }

  ngOnInit(): void {
    this.initialisePlaceholders();
    this.getHelpLink();
    this.buttonLinkForm.patchValue({
      buttonLink: this.template?.buttonLink
    });
  }

  getHelpLink(): void {
    this.helpLink = "https://help.digitalpractice.vet";
  }

  initialisePlaceholders(): void {
    if (this.manualPlaceholders) {
      this.placeholdersForm = new UntypedFormGroup({});
      this.manualPlaceholders.forEach((p) => {
        let value = '';
        switch (p.label) {
          case MergeFieldContent.PracticeName:
            value = this.conversationService.practice?.name ?? '';
            break;
          case MergeFieldContent.ClientName:
            value = this.client?.fullName ?? '';
            break;
          case MergeFieldContent.ClientTitle:
              value = this.client?.title ?? '';
              break;
          case MergeFieldContent.ClientFirstName:
              value = this.client?.firstName ?? '';
              break;
          case MergeFieldContent.ClientLastName:
            value = this.client?.lastName ?? '';
            break;
          case MergeFieldContent.ClientPostcode:
            value = this.client?.postcode ?? '';
            break;
          case MergeFieldContent.ClientBalance:
            const currencySymbol = this.practiceCurrency?.currencySymbol || '£';
            if (this.client?.accountStatus) {
              const balance = parseFloat(this.client.accountStatus);
              value = `${currencySymbol}${balance.toFixed(2)}`;
            } else {
              value = currencySymbol;
            }
            break;
          case MergeFieldContent.PatientName:
            value = this.patient?.name ?? '';
            break;
          case MergeFieldContent.PracticeEmail:
            value = this.conversationService.practice?.email ?? '';
            break;
          case MergeFieldContent.PracticePhone:
            value = this.conversationService.practice?.phoneNumber ?? '';
            break;
          case MergeFieldContent.PracticeWebsite:
            value = this.conversationService.practice?.websiteUrl ?? '';
            break;
          default:
            value = '';
            break;
        }

        this.placeholdersForm.addControl(
          p.id.toString(),
          new UntypedFormControl(value, Validators.required)
        );
      });
    }
  }

  ngOnDestroy(): void {
    this.alive = false;
  }

  selectMedia(): void {
    if (this.template) {
      this.translateService.get('ui.replace_placeholder.select_media').pipe(takeWhile(_ => this.alive)).subscribe(header => {
        this.selectMediaDialog = this.dialogService.open(MediaSelectorComponent, {
          header,
          modal: true,
          width: '1000px',
          baseZIndex: 10000,
          data: {
            type: this.template!.mediaType,
          },
        });

        this.selectMediaDialog.onClose.subscribe((media: Media) => {
          if (media) {
            this.setSelectedMedia(media);
          }
        });
      })
    }
  }

  setSelectedMedia(media: Media): void {
    this.previewSrc = null;
    this.selectedMedia = media;

    if (media.mime.includes('image/')) {
      this.previewType = 'image';
      this.previewSrc = media.signedUrl;
    } else if (media.mime.includes('video/')) {
      this.previewType = 'video';
      this.previewSrc = media.signedUrl;
    } else if (media.mime.includes('pdf')) {
      this.previewType = 'pdf';
      this.previewSrc = media.signedUrl;
    }

    this.mediaName = this.formatMediaName(media);
  }

  formatMediaName(media: { mime: string; name: string }): string {
    const extension = media.mime.split('/')[1]; // Get the media extension from the MIME type
    return `${media.name}.${extension}`;
  }

  removeMedia(): void {
    this.previewSrc = null;
    this.selectedMedia = undefined;
    this.mediaName ='';
  }

  submitManualPlaceholders(): void {
    if (this.placeholdersForm.valid) {
      this.error = false;

      const placeholders: { placeholderId: string; replacement: string }[] = [];
      Object.keys(this.placeholdersForm.controls).forEach((key) => {
        placeholders.push({
          placeholderId: key,
          replacement: this.placeholdersForm.get(key)?.value || '',
        });
      });

      if (this.template) {
        if (this.template.attachMedia && !this.selectedMedia) {
          this.error = true;
        } else {
          this.emitSelectTemplate(
            this.template,
            placeholders,
            this.selectedMedia,
            this.previewType,
            this.buttonLinkForm.get('buttonLink')?.value
          );
        }
      }
    } else {
      this.error = true;
    }
  }

  private getMergeFieldReplacement(
    mergeField: TemplateMergeField,
    placeholders: { placeholderId: string; replacement: string }[]
  ): string {
    const userReplacement = placeholders.find(
      (p) => p.placeholderId === mergeField.id.toString()
    )?.replacement;
    
    if (userReplacement !== undefined && userReplacement.trim() !== '') {
      return userReplacement;
    }
    
    switch (mergeField.content) {
      case MergeFieldContent.PracticeName:
        return (
          this.conversationService.practice?.name ||
          mergeField.placeholder
        );

      case MergeFieldContent.ClientName:
        return (
          mergeField.placeholder
        );
      case MergeFieldContent.ClientTitle:
        return (
          mergeField.placeholder
        );
  
      case MergeFieldContent.ClientFirstName:
        return (
          mergeField.placeholder
        );
  
      case MergeFieldContent.ClientLastName:
        return (
          mergeField.placeholder
        );

      case MergeFieldContent.ClientPostcode:
        return (
          this.client?.postcode ||
          mergeField.placeholder
        );

      case MergeFieldContent.ClientBalance:
        const currencySymbol = this.practiceCurrency?.currencySymbol || '£';
        if (this.client?.accountStatus) {
          const balance = parseFloat(this.client.accountStatus);
          // If currencySymbol is undefined (fallback to £), don't add decimals
          if (!this.practiceCurrency?.currencySymbol) {
            return `${currencySymbol}${balance}`;
          }
          return `${currencySymbol}${balance.toFixed(2)}`;
        }
        return currencySymbol;

      case MergeFieldContent.PracticeEmail:
        return (
          this.conversationService.practice?.email ||
          mergeField.placeholder
        );

      case MergeFieldContent.PracticePhone:
        return (
          this.conversationService.practice?.phoneNumber ||
          mergeField.placeholder
        );

      case MergeFieldContent.PracticeWebsite:
        return (
          this.conversationService.practice?.websiteUrl ||
          mergeField.placeholder
        );

      case MergeFieldContent.AppointmentInformation:
        return (
          mergeField.placeholder
        );

      case MergeFieldContent.ProductInformation:
        return (
          placeholders.find(
            (p) => p.placeholderId === mergeField.id.toString()
          )?.replacement ||
          mergeField.placeholder
        );

      case MergeFieldContent.Custom:
        return (
          mergeField.placeholder
        );
    }

    return mergeField.placeholder;
  }

  getTemplateText(
    template: Template,
    placeholders: { placeholderId: string; replacement: string }[]
  ): string {
    let templateText = template.body;

    template.mergeFields.forEach((mergeField) => {
      templateText = templateText.replace(
        mergeField.placeholder,
        this.getMergeFieldReplacement(mergeField, placeholders)
      );
    });

    return templateText;
  }

  ngAfterViewInit(): void {
    if (this.manualPlaceholders) {
      for (let manualplaceholder of this.manualPlaceholders) {
        this.placeholdersForm.controls[manualplaceholder.id].valueChanges.subscribe((value) => {
          this.updatePlaceholders();
        });
      }
      this.updatePlaceholders();
    }
  }
  
  private updatePlaceholders(): void {
    const placeholders: { placeholderId: string; replacement: string }[] = [];
    Object.keys(this.placeholdersForm.controls).forEach((key) => {
      placeholders.push({
        placeholderId: key,
        replacement: this.placeholdersForm.get(key)?.value || '',
      });
    });
    
    if (this.template) {
      this.conversationService.updatePreviewConversation(this.template, placeholders);
    }
  }

  emitSelectTemplate(
    template: Template,
    placeholders: { placeholderId: string; replacement: string }[],
    media?: Media,
    previewType?: string,
    buttonLink?: string
  ): void {
    this.result = {
      ...this.result,
      template,
      placeholders,
      media,
      previewType,
      buttonLink
    };
    this.conversationService.updatePreviewConversation(template, placeholders)
    this.ref.close(this.result);
  }

  onButtonLinkChange(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    let value = inputElement.value.trim();
  
    if (!value.startsWith('https://') && !value.startsWith('http://')) {
      value = `https://${value}`;
    }
  
    this.buttonLinkForm.patchValue({
      buttonLink: value
    });
  }
  
  subscribeToCurrentPractice(): void {
    this.store
      .pipe(select(getCurrentPractice))
      .pipe(takeWhile(() => this.alive))
      .subscribe((practice) => {
        if(practice){
          this.practice = practice;
          this.theme = practice.theme ? practice.theme : Theme.DigitalPractice;
          this.setPracticeCurrency();
        }
      });
  }

  subscribeToCurrencies(): void {
    this.store
      .pipe(select(getCurrencies))
      .pipe(takeWhile(() => this.alive))
      .subscribe((currencies) => {
        this.currencies = currencies;
        this.setPracticeCurrency();
      });
  }

  setPracticeCurrency(): void {
    if (this.currencies.length && this.practice) {
      this.practiceCurrency = this.currencies.find(
        (currency) => currency.currencyCode === this.practice?.currency,
      );
    }
  }
}
